import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWorkspace } from '@/api/workspace'
import type { WorkspacePath } from '@/components/app-shell/nav-config'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { isSetupChecklistHidden, showSetupChecklist } from '@/lib/setup-checklist'

// Panduan (09 Onboarding, #231): a static page for Owner and Staff that explains the stock flow,
// who does which step and where, and the key terms. Connecting an AI app has its own page (./mcp). All text comes from `guide.*` in id.json; the
// link labels reuse each screen's own title. A link to an Owner-only screen shows to Staff as text
// with "(Pemilik)", since that screen would only send them back (permissions.md).
export const Route = createFileRoute('/w/$slug/guide/')({
  component: GuideScreen,
  staticData: { breadcrumb: 'guide.title' },
})

type GuideLink = { labelKey: string; to: WorkspacePath; ownerOnly?: boolean }
type GuideStep = { key: string; links: GuideLink[] }

// The main flow, in order (the diagram and the step cards).
const FLOW: GuideStep[] = [
  {
    key: 'pools',
    links: [
      { labelKey: 'materials.title', to: '/w/$slug/materials' },
      { labelKey: 'restocks.title', to: '/w/$slug/materials/restocks' },
      { labelKey: 'settings.openingBalances.title', to: '/w/$slug/settings/opening-balances', ownerOnly: true },
    ],
  },
  { key: 'batch', links: [{ labelKey: 'batches.title', to: '/w/$slug/batches' }] },
  { key: 'wip', links: [{ labelKey: 'stock.wip.title', to: '/w/$slug/stock/wip' }] },
  { key: 'assembly', links: [{ labelKey: 'assemblies.title', to: '/w/$slug/assemblies' }] },
  { key: 'finishedGoods', links: [{ labelKey: 'stock.title', to: '/w/$slug/stock' }] },
  { key: 'dispatch', links: [{ labelKey: 'dispatches.title', to: '/w/$slug/dispatches' }] },
  {
    key: 'channelStock',
    links: [
      { labelKey: 'stock.title', to: '/w/$slug/stock' },
      { labelKey: 'returns.title', to: '/w/$slug/returns' },
    ],
  },
  { key: 'salesReport', links: [{ labelKey: 'salesReports.title', to: '/w/$slug/sales-reports' }] },
  {
    key: 'payment',
    links: [
      { labelKey: 'salesReports.title', to: '/w/$slug/sales-reports' },
      { labelKey: 'payments.outstandingScreen.title', to: '/w/$slug/sales-reports/outstanding', ownerOnly: true },
    ],
  },
]

// Beside the main flow: money out, and the cost per unit the flow produces.
const ASIDE: GuideStep[] = [
  { key: 'expenses', links: [{ labelKey: 'expenses.title', to: '/w/$slug/expenses' }] },
  {
    key: 'cogs',
    links: [
      { labelKey: 'cogs.title', to: '/w/$slug/cogs' },
      { labelKey: 'cogs.settings.title', to: '/w/$slug/settings/cogs', ownerOnly: true },
    ],
  },
]

// What has to exist before the flow runs (the Setup checklist's steps, 09 Onboarding).
const SETUP: GuideStep[] = [
  { key: 'adoption', links: [{ labelKey: 'settings.workspace.title', to: '/w/$slug/settings/workspace', ownerOnly: true }] },
  {
    key: 'pools',
    links: [
      { labelKey: 'materials.title', to: '/w/$slug/materials' },
      { labelKey: 'restocks.title', to: '/w/$slug/materials/restocks' },
      { labelKey: 'settings.openingBalances.title', to: '/w/$slug/settings/opening-balances', ownerOnly: true },
    ],
  },
  { key: 'machines', links: [{ labelKey: 'machines.title', to: '/w/$slug/machines' }] },
  { key: 'catalog', links: [{ labelKey: 'products.title', to: '/w/$slug/products' }] },
  { key: 'channels', links: [{ labelKey: 'salesChannels.title', to: '/w/$slug/channels' }] },
  { key: 'vendorPrices', links: [{ labelKey: 'vendorPrices.title', to: '/w/$slug/vendor-prices', ownerOnly: true }] },
  { key: 'cogsSettings', links: [{ labelKey: 'cogs.settings.title', to: '/w/$slug/settings/cogs', ownerOnly: true }] },
]

const GLOSSARY = [
  'pool',
  'restock',
  'openingBalance',
  'adoptionDate',
  'product',
  'model',
  'variant',
  'bom',
  'batch',
  'wip',
  'assembly',
  'finishedGoods',
  'channel',
  'dispatch',
  'channelStock',
  'salesReport',
  'vendorPrice',
  'payment',
  'expense',
  'cogs',
  'adjustment',
  'void',
]

function GuideScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const navigate = useNavigate()
  const workspace = useWorkspace(slug)
  const isOwner = workspace.data?.role === 'owner'
  // The checklist only exists while onboarding is open (09 Onboarding, #230).
  const onboardingComplete = Boolean(workspace.data?.onboarding.onboarding_completed_at)
  const [checklistHidden] = useState(() => isSetupChecklistHidden(slug))

  function showChecklist() {
    showSetupChecklist(slug)
    void navigate({ to: '/w/$slug', params: { slug } })
  }

  return (
    <section className="flex max-w-4xl flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="font-heading text-xl font-semibold">{t('guide.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('guide.intro')}</p>
        </div>
        {isOwner && checklistHidden && !onboardingComplete ? (
          <Button variant="outline" onClick={showChecklist}>
            {t('guide.showChecklist')}
          </Button>
        ) : null}
      </div>

      <FlowDiagram />

      <section className="flex flex-col gap-3" aria-labelledby="guide-setup">
        <h2 id="guide-setup" className="font-heading text-lg font-semibold">
          {t('guide.setup.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('guide.setup.intro')}</p>
        <div className="grid gap-3 md:grid-cols-2">
          {(['fromScratch', 'existingStock'] as const).map((track) => (
            <Card key={track} size="sm">
              <CardHeader>
                <CardTitle className="text-base">{t(`guide.setup.tracks.${track}.title`)}</CardTitle>
                <CardDescription>{t(`guide.setup.tracks.${track}.body`)}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
        <CostWarning />
        <Card>
          <CardContent>
            <ol className="flex flex-col divide-y">
              {SETUP.map((item, index) => (
                <li key={item.key} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
                  <p className="text-sm font-medium">
                    {index + 1}. {t(`guide.setup.items.${item.key}.title`)}
                  </p>
                  <p className="text-sm text-muted-foreground">{t(`guide.setup.items.${item.key}.body`)}</p>
                  <StepMeta slug={slug} isOwner={isOwner} who={t(`guide.setup.items.${item.key}.who`)} links={item.links} />
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="guide-steps">
        <h2 id="guide-steps" className="font-heading text-lg font-semibold">
          {t('guide.steps.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('guide.steps.intro')}</p>
        <div className="grid gap-3 md:grid-cols-2">
          {FLOW.map((step, index) => (
            <StepCard key={step.key} slug={slug} isOwner={isOwner} step={step} marker={String(index + 1)} />
          ))}
          {ASIDE.map((step) => (
            <StepCard key={step.key} slug={slug} isOwner={isOwner} step={step} marker="+" />
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="guide-glossary">
        <h2 id="guide-glossary" className="font-heading text-lg font-semibold">
          {t('guide.glossary.title')}
        </h2>
        <Card>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-3 md:grid-cols-2">
              {GLOSSARY.map((key) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <dt className="text-sm font-medium">{t(`guide.glossary.terms.${key}.term`)}</dt>
                  <dd className="text-sm text-muted-foreground">{t(`guide.glossary.terms.${key}.definition`)}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </section>
    </section>
  )
}

// The flow as boxes and arrows: down on a phone, left to right (wrapping) from `sm`. Each box jumps
// to its step card. Expenses and COGS sit beside the main flow, dashed.
function FlowDiagram() {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('guide.flow.title')}</CardTitle>
        <CardDescription>{t('guide.flow.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ol className="flex flex-col items-stretch gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-y-3">
          {FLOW.map((step, index) => (
            <li key={step.key} className="flex flex-col items-center gap-1 sm:flex-row">
              <a
                href={`#guide-step-${step.key}`}
                className="flex w-full items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm font-medium hover:bg-muted sm:w-auto"
              >
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {index + 1}
                </span>
                {t(`guide.flow.nodes.${step.key}`)}
              </a>
              {index < FLOW.length - 1 ? (
                <ArrowRight aria-hidden className="size-4 shrink-0 rotate-90 text-muted-foreground sm:rotate-0" />
              ) : null}
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">{t('guide.flow.aside')}</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {ASIDE.map((step) => (
              <li key={step.key}>
                <a
                  href={`#guide-step-${step.key}`}
                  className="flex h-full flex-col gap-0.5 rounded-lg border border-dashed px-3 py-2 hover:bg-muted"
                >
                  <span className="text-sm font-medium">{t(`guide.flow.nodes.${step.key}`)}</span>
                  <span className="text-xs text-muted-foreground">{t(`guide.flow.asideNotes.${step.key}`)}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}

// Cost into Pools before the first Batch: a Batch takes grams at the Pool's average cost, so a
// Pool that never had a cost gives the Batch, and the COGS, no material value (09 Onboarding).
function CostWarning() {
  const { t } = useTranslation()

  return (
    <div
      role="note"
      className="flex gap-3 rounded-xl border border-amber-500/50 bg-amber-50 p-4 text-sm dark:bg-amber-950/30"
    >
      <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">{t('guide.setup.costWarning.title')}</p>
        <p className="text-muted-foreground">{t('guide.setup.costWarning.body')}</p>
      </div>
    </div>
  )
}

function StepCard({
  slug,
  isOwner,
  step,
  marker,
}: {
  slug: string
  isOwner: boolean
  step: GuideStep
  marker: string
}) {
  const { t } = useTranslation()

  return (
    <Card id={`guide-step-${step.key}`} size="sm" className="scroll-mt-20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {marker}
          </span>
          {t(`guide.flow.nodes.${step.key}`)}
        </CardTitle>
        <CardDescription>{t(`guide.steps.${step.key}.body`)}</CardDescription>
      </CardHeader>
      <CardContent>
        <StepMeta slug={slug} isOwner={isOwner} who={t(`guide.steps.${step.key}.who`)} links={step.links} />
      </CardContent>
    </Card>
  )
}

function StepMeta({ slug, isOwner, who, links }: { slug: string; isOwner: boolean; who: string; links: GuideLink[] }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1 text-sm">
      <p>
        <span className="text-muted-foreground">{t('guide.who')}</span> {who}
      </p>
      <p className="flex flex-wrap gap-x-3 gap-y-1">
        <span className="text-muted-foreground">{t('guide.screens')}</span>
        {links.map((link) =>
          link.ownerOnly && !isOwner ? (
            <span key={link.to} className="text-muted-foreground">
              {t(link.labelKey)} {t('guide.ownerOnly')}
            </span>
          ) : (
            <Link
              key={link.to}
              to={link.to}
              params={{ slug }}
              className="font-medium underline underline-offset-4 hover:text-primary"
            >
              {t(link.labelKey)}
            </Link>
          ),
        )}
      </p>
    </div>
  )
}
